import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesJimengDreamActorM1Params {
  /**
   * 参考视频URL
   */
  video_url: string;
  /**
   * 图像URL
   */
  image_url: string;
  /**
   * 随机种子，必须是-1或正整数
   * @default -1
   */
  seed?: number;
  /**
   * 视频质量，可选值：'normal'（普通）、'high'（高质量）
   * @values normal | high
   */
  video_quality?: 'normal' | 'high';
  /**
   * 水印信息配置
   */
  logo_info?: {
    /**
     * 是否添加水印
     * @default false
     */
    add_logo?: boolean;
    /**
     * 水印位置
     * @default 0
     */
    position?: number;
    /**
     * 水印语言
     * @default 0
     */
    language?: number;
    /**
     * 水印不透明度
     * @default 1
     */
    opacity?: number;
    /**
     * 水印文本内容
     * @default ""
     */
    logo_text_content?: string;
  };
  /**
   * 是否返回URL
   * @default true
   */
  return_url?: boolean;
  /**
   * AIGC元数据
   */
  aigc_meta?: {
    /**
     * 内容生产者
     * @default "unifygen-api"
     */
    content_producer?: string;
    /**
     * 生产者ID
     * @default "unifygen-api"
     */
    producer_id?: string;
    /**
     * 水印文本内容
     * @default ""
     */
    logo_text_content?: string;
  };
}

export interface VolcesJimengDreamActorM1Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_jimeng_dream_actor_m1
 *
 * Volces Jimeng Dream Actor M1 Video Generator
 * 
 * Video generation with reference video and image (即梦造梦演员M1视频生成).
 * 
 * Model: dream_actor_m1
 * Features: 视频生成（参考视频+图像融合）
 * Quality: normal（普通）, high（高质量）
 * Supports: 随机种子控制、视频质量选择
 * 
 * API Documentation: https://www.volcengine.com/docs/85621/1798351
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesJimengDreamActorM1 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesJimengDreamActorM1({
 *   // 参数见 VolcesJimengDreamActorM1Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesJimengDreamActorM1Params 接口定义
 * @returns 生成的资源列表
 */
export async function volcesJimengDreamActorM1(
  params: VolcesJimengDreamActorM1Params,
): Promise<VolcesJimengDreamActorM1Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_jimeng_dream_actor_m1',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesJimengDreamActorM1Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
